import {
  DeleteResult,
  InsertResult,
  PullResult,
  PushObjectRecord,
  PushResult,
  UpdateResult,
} from '@shared/providers/provider-transport';
import { request } from '@v2/api/utils';
import { Highland } from '@v2/utils/highland';
import { appEventBus } from 'shared/infrastructure/app-event-bus';
import { ProviderClient, ProviderRemoteChunkEvent } from 'shared/providers/provider';
import { DataField, DataObject } from 'shared/providers/types';
import { Flowstep, ProviderType } from 'shared/types/flowstep';
import { consumer } from 'shared/utils/stream-utils';
import { v4 as uuid } from 'uuid';
import { fieldTypesToSymbolsCount } from 'shared/excel/runtime/flowstepExecution';
import { CancelError, CancelToken } from 'shared/utils/cancelToken';

const objectPropsMap = new Map<string, DataField[]>();
const objectTypesMap = new Map<string, DataObject[]>();

const DEFAULT_MAX_RECORDS_PER_REQUEST = 5_000;

type RemoteApiProviderOptions = {
  maxRecordsPerRequest?: number;
};

export class RemoteApiProvider implements ProviderClient {
  constructor(
    private readonly token: string,
    private readonly providerType: ProviderType,
    private readonly options: RemoteApiProviderOptions = {
      maxRecordsPerRequest: DEFAULT_MAX_RECORDS_PER_REQUEST,
    },
  ) {}

  private async startRequest(requestId: string, abortController?: AbortController) {
    try {
      await request('POST', '/request/start', { requestId }, this.token, abortController?.signal);
    } catch (error: any) {
      console.log(`Could not start request. ${error.message}`);
    }
  }

  private async cancelRequest(requestId: string, abortController?: AbortController) {
    try {
      await request('POST', '/request/cancel', { requestId }, this.token, abortController?.signal);
    } catch (error: any) {
      console.log(`Could not cancel request. ${error.message}`);
    }
  }

  private async consumeResponse<T>(
    push: (error: Error | null, data?: T) => void,
    response: Response,
    cancelToken?: CancelToken,
  ) {
    const reader = response.body!.pipeThrough(new TextDecoderStream()).getReader();

    const cancelPromise = createCancelPromise(cancelToken);

    let buffer = '';
    let isDone = false;

    while (!isDone) {
      const result = await Promise.race([reader.read(), cancelPromise]);

      if (!result) {
        await reader.cancel('Canceled by user');
        break;
      }

      const { value, done } = result;
      isDone = done;

      if (value) {
        buffer += value;
      }

      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (!line) continue;

        const object = JSON.parse(line);
        if (object.type === 'error') {
          push(new Error(object.payload.error?.message || 'Error during execution of flowstep'));
        } else {
          push(null, object);
        }
      }
    }
  }

  private async pushFlowstepData<T extends PushResult>(
    flowstep: Flowstep,
    objectStream: Highland.Stream<PushObjectRecord>,
    cancelToken?: CancelToken,
  ): Promise<Highland.Stream<T>> {
    let pushObjects: any[] = [];

    const abortController = cancelToken && new AbortController();
    const cancelPromise = createCancelPromise(cancelToken, abortController);

    let requestId = '';

    return objectStream
      .consume<T>(
        consumer<PushObjectRecord, T>(async (error, value, push, next) => {
          if (error) {
            push(error, Highland.nil);
          }

          if (cancelToken?.isCanceled()) {
            push(null, Highland.nil);
            return;
          }

          if (
            pushObjects.length >= this.options.maxRecordsPerRequest! ||
            (value === Highland.nil && pushObjects.length > 0)
          ) {
            if (requestId) {
              await this.cancelRequest(requestId, abortController);
            }
            requestId = uuid();
            await this.startRequest(requestId, abortController);

            appEventBus.notify(new ProviderRemoteChunkEvent(pushObjects.length));

            const response = await Promise.race([
              cancelPromise,
              request(
                'POST',
                '/execute-flowstep',
                {
                  flowstep,
                  pushObjects,
                  requestId,
                  requestCheckingPeriod: 3000,
                },
                this.token,
                abortController?.signal,
              ),
            ]);

            pushObjects = [];

            if (response) {
              await this.consumeResponse<T>(push, response, cancelToken);
            }
          }

          if (value === Highland.nil) {
            push(null, Highland.nil);
            return;
          }

          if (pushObjects.length < this.options.maxRecordsPerRequest!) {
            pushObjects.push(value);
          }
          next();
        }),
      )
      .consume((error, value, push, next) => {
        if (error || value === Highland.nil) {
          if (requestId) {
            this.cancelRequest(requestId);
          }
          console.log(error?.name);
          if (error && error.name !== 'AbortError') {
            push(error);
          }

          push(null, Highland.nil);
          return;
        }

        push(null, value);
        next();
      });
  }

  async getFlowstepData(flowstep: Flowstep, cancelToken?: CancelToken): Promise<Highland.Stream<PullResult>> {
    const abortController = cancelToken && new AbortController();
    const cancelPromise = createCancelPromise(cancelToken, abortController);

    const requestId = uuid();
    await this.startRequest(requestId, abortController);

    return Highland(async (push) => {
      try {
        const response = await Promise.race([
          cancelPromise,
          request(
            'POST',
            '/execute-flowstep',
            {
              flowstep,
              requestId,
              requestCheckingPeriod: 3000,
            },
            this.token,
            abortController?.signal,
          ),
        ]);
        if (response) {
          await this.consumeResponse(push, response, cancelToken);
        }
        push(null, Highland.nil);
      } catch (error: any) {
        console.log(error?.name);
        if (error.name !== 'AbortError') {
          push(error);
        }
        push(null, Highland.nil);
      } finally {
        if (requestId) {
          await this.cancelRequest(requestId);
        }
      }
    });
  }

  async insertFlowstepData(
    flowstep: Flowstep,
    objectStream: Highland.Stream<PushObjectRecord>,
    cancelToken?: CancelToken,
  ): Promise<Highland.Stream<InsertResult>> {
    return this.pushFlowstepData<InsertResult>(flowstep, objectStream, cancelToken);
  }

  async updateFlowstepData(
    flowstep: Flowstep,
    objectStream: Highland.Stream<PushObjectRecord>,
    cancelToken?: CancelToken,
  ): Promise<Highland.Stream<UpdateResult>> {
    return this.pushFlowstepData<UpdateResult>(flowstep, objectStream, cancelToken);
  }

  async deleteFlowstepData(
    flowstep: Flowstep,
    objectStream: Highland.Stream<PushObjectRecord>,
    cancelToken?: CancelToken,
  ): Promise<Highland.Stream<DeleteResult>> {
    return this.pushFlowstepData<DeleteResult>(flowstep, objectStream, cancelToken);
  }

  async loadObjectFields(objectType: string) {
    const key = `${this.providerType}:${objectType}`;
    if (!objectPropsMap.has(key)) {
      const response = await request(
        'GET',
        `/object-props?objectType=${objectType}&provider=${this.providerType}`,
        {},
        this.token,
      );

      const result = await response.json();
      objectPropsMap.set(key, result);
    }

    return objectPropsMap.get(key)!;
  }

  async loadObjectTypes() {
    if (!objectTypesMap.has(this.providerType)) {
      const response = await request('GET', `/object-types?provider=${this.providerType}`, {}, this.token);

      const result = await response.json();

      objectTypesMap.set(this.providerType, result);
    }

    return objectTypesMap.get(this.providerType)!;
  }

  async buildUrl(requestData: { flowstep?: Flowstep; objectId?: string }): Promise<string> {
    const response = await request('POST', '/build-provider-url', requestData, this.token);
    return await response.text();
  }

  estimateDataSize(fields: DataField[]) {
    return fieldTypesToSymbolsCount(fields.map((field) => field.type));
  }
}

const createCancelPromise = (cancelToken?: CancelToken, abortSignal?: AbortController) =>
  cancelToken
    ? new Promise<void>((resolve) => {
        if (cancelToken.isCanceled()) {
          resolve();
          return;
        }

        cancelToken.onAbort(() => {
          try {
            abortSignal?.abort(new CancelError());
          } catch (error: any) {
            console.log(error);
          }
          resolve();
        });
      })
    : new Promise<void>(() => {});
